import { useCallback, useRef } from 'react';
import { useEventsApi } from 'services/api';
import type { EventsApi } from 'services/api/apis';

const FLUSH_DELAY_MS = 3000;
const DWELL_MS = 1000;
const VISIBILITY_THRESHOLD = 0.5;
const MAX_BATCH_SIZE = 100;

// Module-scoped state. Dedupe is per page-load (cleared on full navigation).
const seenEventIds = new Set<string>();
const pendingEventIds = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let apiRef: EventsApi | null = null;
let pageHideRegistered = false;

const flushNow = (): void => {
  if (!apiRef || pendingEventIds.size === 0) return;

  const ids = Array.from(pendingEventIds).slice(0, MAX_BATCH_SIZE);
  ids.forEach(id => pendingEventIds.delete(id));

  void apiRef.recordEventImpressions(ids);

  if (pendingEventIds.size > 0)
    scheduleFlush();

};

const scheduleFlush = (): void => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushNow();
  }, FLUSH_DELAY_MS);
};

const enqueue = (eventId: string): void => {
  if (seenEventIds.has(eventId)) return;
  seenEventIds.add(eventId);
  pendingEventIds.add(eventId);
  scheduleFlush();
};

const ensurePageHideListener = (): void => {
  if (pageHideRegistered || typeof window === 'undefined') return;
  pageHideRegistered = true;

  const onHide = (): void => {
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flushNow();
  };

  window.addEventListener('pagehide', onHide);
  window.addEventListener('beforeunload', onHide);
};

export const useImpressionTracker = (
  eventId: string | undefined,
): ((node: HTMLElement | null) => void) => {
  const eventsApi = useEventsApi();
  apiRef = eventsApi;

  if (typeof window !== 'undefined')
    ensurePageHideListener();


  const observerRef = useRef<IntersectionObserver | null>(null);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  return useCallback(
    (node: HTMLElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (dwellTimerRef.current) {
        clearTimeout(dwellTimerRef.current);
        dwellTimerRef.current = null;
      }

      if (!node || !eventId) return;
      if (typeof IntersectionObserver === 'undefined') return;
      if (seenEventIds.has(eventId)) return;

      const observer = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            const sufficientlyVisible =
              entry.isIntersecting &&
              entry.intersectionRatio >= VISIBILITY_THRESHOLD;

            if (sufficientlyVisible) {
              if (dwellTimerRef.current || seenEventIds.has(eventId)) continue;
              dwellTimerRef.current = setTimeout(() => {
                dwellTimerRef.current = null;
                enqueue(eventId);
                observer.disconnect();
                observerRef.current = null;
              }, DWELL_MS);
            } else if (dwellTimerRef.current) {
              clearTimeout(dwellTimerRef.current);
              dwellTimerRef.current = null;
            }
          }
        },
        { threshold: VISIBILITY_THRESHOLD },
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [eventId],
  );
};
